/**
 * Sample product data for Product Integration POC
 * Based on Command Center Products.csv
 */

import { Product, Connection, Interface, IntegrationMap } from '../types/productIntegration';

// Helper function to get icon for a product
export const getProductIcon = (product: Product): string => {
  console.log(`🔍 Getting icon for product: "${product.name}"`);
  
  // Use the product's icon field if specified
  if (product.icon) {
    console.log(`✅ Using product icon: ${product.name} -> ${product.icon}`);
    return product.icon;
  }
  
  console.log(`⚠️ No icon specified for product: "${product.name}"`);
  
  // Fallback logic - use MSI icon for internal products, external.png for external
  let fallbackIcon = product.type === 'internal' ? 'msi_icon.png' : 'external.png';
  
  // Special fallback logic based on product characteristics
  const lowerName = product.name.toLowerCase();
  if (lowerName.includes('svx')) {
    fallbackIcon = 'svx.png';
  } else if (lowerName.includes('apx') || lowerName.includes('radio')) {
    fallbackIcon = 'apx-next.png';
  } else if (lowerName.includes('assist') && lowerName.includes('ai')) {
    fallbackIcon = 'assist.png';
  }
  
  console.log(`🔄 Using fallback for "${product.name}": ${fallbackIcon}`);
  return fallbackIcon;
};

export const SAMPLE_PRODUCTS: Product[] = [
  // Internal MSI Products
  {
    id: 'msi-001',
    name: '10-21',
    category: 'Incident Management',
    team: 'Field Response and Reporting',
    type: 'internal',
    icon: 'msi_icon.png', // Generic MSI icon for internal products
    productContact: 'John Smith',
    engineeringContact: 'Jane Doe',
    status: 'active'
  },
  {
    id: 'msi-002',
    name: 'CallWorks ECH',
    category: 'Incident Awareness',
    team: 'Emergency Call Management',
    type: 'internal',
    icon: 'ECH.png',
    productContact: 'Mike Johnson',
    engineeringContact: 'Sarah Wilson',
    status: 'active'
  },
  {
    id: 'msi-003',
    name: 'Callyo',
    category: 'Incident Management',
    team: 'Analysis and Investigation',
    type: 'internal',
    icon: 'callyo.png',
    productContact: 'David Brown',
    engineeringContact: 'Lisa Chen',
    status: 'active'
  },
  {
    id: 'msi-004',
    name: 'CAPE',
    category: 'Incident Management',
    team: 'Real-Time Intelligence Operations',
    type: 'internal',
    icon: 'cape.png',
    productContact: 'Robert Taylor',
    engineeringContact: 'Maria Garcia',
    status: 'active'
  },
  {
    id: 'msi-005',
    name: 'CC 911',
    category: 'Incident Awareness',
    team: 'Emergency Call Management',
    type: 'internal',
    icon: 'ECH.png',
    productContact: 'Jennifer Lee',
    engineeringContact: 'Tom Anderson',
    status: 'active'
  },
  {
    id: 'msi-006',
    name: 'CC Analytics',
    category: 'Post-Incident Resolution',
    team: 'Analysis and Investigation',
    type: 'internal',
    icon: 'cc_analytics.png',
    productContact: 'Steven Clark',
    engineeringContact: 'Amy Rodriguez',
    status: 'active'
  },
  {
    id: 'msi-007',
    name: 'CC Aware',
    category: 'Incident Management',
    team: 'Real-Time Intelligence Operations',
    type: 'internal',
    icon: 'cc_aware.png',
    productContact: 'Kevin White',
    engineeringContact: 'Nicole Davis',
    status: 'active'
  },
  {
    id: 'msi-008',
    name: 'CC CAD',
    category: 'Incident Management',
    team: 'Voice & Computer-Aided Dispatch',
    type: 'internal',
    icon: 'cc_cad.png',
    productContact: 'Brian Miller',
    engineeringContact: 'Laura Thompson',
    status: 'active'
  },
  {
    id: 'msi-009',
    name: 'CC Records',
    category: 'Post-Incident Resolution',
    team: 'Records & Evidence Management',
    type: 'internal',
    icon: 'records.png',
    productContact: 'Chris Evans',
    engineeringContact: 'Rachel Green',
    status: 'active'
  },
  {
    id: 'msi-010',
    name: 'PremierOne CAD',
    category: 'Incident Management',
    team: 'Voice & Computer-Aided Dispatch',
    type: 'internal',
    icon: 'cc_cad.png',
    productContact: 'Daniel Kim',
    engineeringContact: 'Jessica Wong',
    status: 'active'
  },
  {
    id: 'msi-011',
    name: 'PremierOne Records',
    category: 'Post-Incident Resolution',
    team: 'Records & Evidence Management',
    type: 'internal',
    icon: 'records.png',
    productContact: 'Michael Chen',
    engineeringContact: 'Sarah Williams',
    status: 'active'
  },
  {
    id: 'msi-012',
    name: 'Flex CAD',
    category: 'Incident Management',
    team: 'Voice & Computer-Aided Dispatch',
    type: 'internal',
    icon: 'cc_cad.png',
    productContact: 'Alex Rodriguez',
    engineeringContact: 'Emma Martinez',
    status: 'active'
  },
  {
    id: 'msi-013',
    name: 'Flex Records',
    category: 'Post-Incident Resolution',
    team: 'Records & Evidence Management',
    type: 'internal',
    icon: 'records.png',
    productContact: 'James Thompson',
    engineeringContact: 'Lisa Park',
    status: 'active'
  },
  {
    id: 'msi-014',
    name: 'SVX',
    category: 'Video Communications', // Shortened from "Video Remote Speaker Microphone"
    team: 'Critical Communications',
    type: 'internal',
    icon: 'svx.png', // Specific SVX icon
    productContact: 'Mark Stevens',
    engineeringContact: 'Jennifer Walsh',
    status: 'active'
  },
  {
    id: 'msi-015',
    name: 'Assist AI',
    category: 'Artificial Intelligence',
    team: 'AI Solutions',
    type: 'internal',
    icon: 'assist.png', // Specific Assist AI icon
    productContact: 'Dr. Sarah Kim',
    engineeringContact: 'Alex Chen',
    status: 'active'
  },
  {
    id: 'msi-016',
    name: 'APX NEXT Radio',
    category: 'Critical Communication',
    team: 'Critical Communications',
    type: 'internal',
    icon: 'apx-next.png', // Specific APX NEXT icon
    productContact: 'Mike Torres',
    engineeringContact: 'Lisa Rodriguez',
    status: 'active'
  },
  {
    id: 'msi-017',
    name: 'V700 cameras',
    category: 'Video Surveillance',
    team: 'Critical Communications',
    type: 'internal',
    icon: 'v700.png',
    productContact: 'David Park',
    engineeringContact: 'Rachel Liu',
    status: 'active'
  },
  {
    id: 'msi-018',
    name: 'M500 in-car video camera',
    category: 'Mobile Video',
    team: 'Critical Communications',
    type: 'internal',
    icon: 'm500.png', // Video-related products use SVX icon
    productContact: 'Chris Martinez',
    engineeringContact: 'Amy Zhang',
    status: 'active'
  },
  {
    id: 'msi-019',
    name: 'Holster Aware sensors',
    category: 'Sensor Technology',
    team: 'Field Response and Reporting',
    type: 'internal',
    icon: 'msi_icon.png',
    productContact: 'Kevin Brown',
    engineeringContact: 'Maria Gonzalez',
    status: 'active'
  },
  {
    id: 'msi-020',
    name: 'CC DEMS',
    category: 'Incident Management',
    team: 'Emergency Data Management',
    type: 'internal',
    icon: 'evidence.png',
    productContact: 'Janet Wilson',
    engineeringContact: 'Robert Kim',
    status: 'active'
  },
  {
    id: 'msi-021',
    name: 'CC Responder',
    category: 'Incident Management',
    team: 'Field Response and Reporting',
    type: 'internal',
    icon: 'msi_icon.png',
    productContact: 'Tony Nguyen',
    engineeringContact: 'Sandra Miller',
    status: 'active'
  },
  
  // External Products (Examples)
  {
    id: 'ext-001',
    name: 'Tellus Hub',
    category: 'Incident Management',
    team: 'Central Square',
    type: 'external',
    icon: 'central_square.png',
    productContact: 'External Contact',
    engineeringContact: 'N/A',
    status: 'active'
  },
  {
    id: 'ext-002',
    name: 'Axon Evidences',
    category: 'Post-Incident Resolution',
    team: 'Axon',
    type: 'external',
    icon: 'axon.png',
    productContact: 'External Contact',
    engineeringContact: 'N/A',
    status: 'active'
  },
  {
    id: 'ext-003',
    name: 'Prosecutor by Karpel',
    category: 'Post-Incident Resolution',
    team: 'Karpel',
    type: 'external',
    icon: 'karpel.png',
    productContact: 'External Contact',
    engineeringContact: 'N/A',
    status: 'active'
  },
  {
    id: 'ext-004',
    name: 'Livescan',
    category: 'Jail',
    team: 'Idemia',
    type: 'external',
    icon: 'idemia.png',
    productContact: 'External Contact',
    engineeringContact: 'N/A',
    status: 'active'
  }
];

export const SAMPLE_CONNECTIONS: Connection[] = [
  {
    id: 'conn-001',
    sourceProductId: 'msi-005', // CC 911
    targetProductId: 'msi-008', // CC CAD
    connectionType: 'single',
    protocol: 'REST API',
    description: 'Emergency call data integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-002',
    sourceProductId: 'msi-008', // CC CAD
    targetProductId: 'msi-007', // CC Aware
    connectionType: 'multiple',
    protocol: 'Event Stream',
    description: 'Real-time incident data sharing',
    status: 'active',
    bidirectional: false
  },
  {
    id: 'conn-003',
    sourceProductId: 'msi-007', // CC Aware
    targetProductId: 'msi-004', // CAPE
    connectionType: 'single',
    protocol: 'GraphQL',
    description: 'Intelligence data integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-004',
    sourceProductId: 'msi-008', // CC CAD
    targetProductId: 'msi-009', // CC Records
    connectionType: 'single',
    protocol: 'Database Sync',
    description: 'Incident record archival',
    status: 'active',
    bidirectional: false
  },
  {
    id: 'conn-005',
    sourceProductId: 'msi-006', // CC Analytics
    targetProductId: 'ext-001', // Microsoft Azure
    connectionType: 'multiple',
    protocol: 'Azure SDK',
    description: 'Analytics data processing',
    status: 'active',
    bidirectional: false
  },
  {
    id: 'conn-006',
    sourceProductId: 'msi-009', // CC Records
    targetProductId: 'ext-003', // Prosecutor by Karpel
    connectionType: 'single',
    protocol: 'REST API',
    description: 'Customer case management integration',
    status: 'planned',
    bidirectional: true
  },
  {
    id: 'conn-007',
    sourceProductId: 'msi-008', // CC CAD
    targetProductId: 'msi-010', // PremierOne CAD
    connectionType: 'single',
    protocol: 'Data Sync',
    description: 'CAD system integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-008',
    sourceProductId: 'msi-008', // CC CAD
    targetProductId: 'msi-012', // Flex CAD
    connectionType: 'single',
    protocol: 'API Integration',
    description: 'Flex CAD system integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-009',
    sourceProductId: 'msi-009', // CC Records
    targetProductId: 'msi-011', // PremierOne Records
    connectionType: 'single',
    protocol: 'Database Sync',
    description: 'Records system integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-010',
    sourceProductId: 'msi-009', // CC Records
    targetProductId: 'msi-013', // Flex Records
    connectionType: 'single',
    protocol: 'API Integration',
    description: 'Flex Records system integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-012',
    sourceProductId: 'ext-003', // Prosecutor by Karpel
    targetProductId: 'msi-011', // PremierOne Records
    connectionType: 'single',
    protocol: 'REST API',
    description: 'Prosecutor case management to records integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-013',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-015', // Assist AI
    connectionType: 'single',
    protocol: 'AI Integration API',
    description: 'SVX AI-powered voice and video enhancement',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-014',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-016', // APX NEXT Radio
    connectionType: 'single',
    protocol: 'Radio Protocol',
    description: 'SVX integration with APX NEXT radio communications',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-015',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-017', // V700 cameras
    connectionType: 'single',
    protocol: 'Video Stream API',
    description: 'SVX video integration with V700 camera systems',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-016',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-018', // M500 in-car video camera
    connectionType: 'single',
    protocol: 'Mobile Video API',
    description: 'SVX integration with M500 in-car video systems',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-017',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-019', // Holster Aware sensors
    connectionType: 'single',
    protocol: 'Sensor Data API',
    description: 'SVX integration with holster awareness sensors',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-018',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-007', // CC Aware
    connectionType: 'single',
    protocol: 'Event Stream',
    description: 'SVX situational awareness integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-019',
    sourceProductId: 'msi-014', // SVX
    targetProductId: 'msi-020', // CC DEMS
    connectionType: 'single',
    protocol: 'Data Management API',
    description: 'SVX emergency data management integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-020',
    sourceProductId: 'msi-007', // CC Aware
    targetProductId: 'msi-008', // CC CAD
    connectionType: 'multiple',
    protocol: 'Event Stream',
    description: 'Real-time incident awareness to CAD integration',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-021',
    sourceProductId: 'msi-007', // CC Aware
    targetProductId: 'msi-009', // CC Records
    connectionType: 'single',
    protocol: 'Database Sync',
    description: 'Awareness data integration with records system',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-022',
    sourceProductId: 'msi-007', // CC Aware
    targetProductId: 'msi-020', // CC DEMS
    connectionType: 'single',
    protocol: 'Emergency Data API',
    description: 'Situational awareness to emergency data management',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-023',
    sourceProductId: 'msi-007', // CC Aware
    targetProductId: 'msi-021', // CC Responder
    connectionType: 'single',
    protocol: 'Mobile API',
    description: 'Real-time awareness data to field responders',
    status: 'active',
    bidirectional: true
  },
  {
    id: 'conn-024',
    sourceProductId: 'ext-002', // Axon Evidences
    targetProductId: 'msi-020', // CC DEMS
    connectionType: 'single',
    protocol: 'REST API',
    description: 'Evidence data synchronization between Axon and Command Center DEMS',
    status: 'active',
    bidirectional: true,
    interface: {
      id: 'if-001',
      name: 'Axon Evidence Integration',
      direction: 'bidirectional',
      protocol: 'REST API',
      description: 'Secure evidence data exchange between Axon platform and MSI DEMS',
      status: 'active',
      authentication: 'OAuth 2.0',
      dataFormat: 'JSON',
      technicalDocumentationUrl: 'https://docs.axon.com/integration/evidence-api',
      categories: ['Evidence Management', 'Data Sync'],
      type: 'External Integration',
      team: 'Digital Evidence Team',
      visibility: 'public'
    }
  }
];

// Sample Interfaces for external-internal integrations
export const SAMPLE_INTERFACES: Interface[] = [
  {
    id: 'if-001',
    name: 'Axon Evidence Integration',
    direction: 'bidirectional',
    protocol: 'REST API',
    description: 'Secure evidence data exchange between Axon platform and MSI DEMS',
    status: 'active',
    authentication: 'OAuth 2.0',
    dataFormat: 'JSON',
    technicalDocumentationUrl: 'https://docs.axon.com/integration/evidence-api',
    categories: ['Evidence Management', 'Data Sync'],
    type: 'External Integration',
    team: 'Axon',
    visibility: 'public'
  },
  {
    id: 'if-002',
    name: 'Prosecutor Records Interface',
    direction: 'outbound',
    protocol: 'SOAP',
    description: 'Case data transfer to prosecutor management systems',
    status: 'active',
    authentication: 'WS-Security',
    dataFormat: 'XML',
    technicalDocumentationUrl: 'https://docs.karpel.com/api/prosecutor-interface',
    categories: ['Case Management', 'Legal Integration'],
    type: 'Government Interface',
    team: 'Records Management Team',
    visibility: 'internal'
  },
  {
    id: 'if-003',
    name: 'Court Records Integration',
    direction: 'bidirectional',
    protocol: 'REST API',
    description: 'Court case and hearing data synchronization',
    status: 'planned',
    authentication: 'API Key',
    dataFormat: 'JSON',
    technicalDocumentationUrl: 'https://docs.courts.gov/integration/api'
  },
  {
    id: 'if-004',
    name: 'Emergency Services Interface',
    direction: 'inbound',
    protocol: 'WebSocket',
    description: 'Real-time emergency alert and response coordination',
    status: 'active',
    authentication: 'Certificate',
    dataFormat: 'JSON',
    technicalDocumentationUrl: 'https://docs.emergency.gov/realtime-api'
  },
  {
    id: 'if-005',
    name: 'Fleet Management Interface',
    direction: 'bidirectional',
    protocol: 'REST API',
    description: 'Vehicle tracking and dispatch integration',
    status: 'active',
    authentication: 'OAuth 2.0',
    dataFormat: 'JSON',
    technicalDocumentationUrl: 'https://docs.fleetmanagement.com/api'
  },
  {
    id: 'if-006',
    name: 'Records Management System Interface',
    direction: 'bidirectional',
    protocol: 'Database Connection',
    description: 'Direct database integration for records synchronization',
    status: 'deprecated',
    authentication: 'Database Auth',
    dataFormat: 'SQL',
    technicalDocumentationUrl: 'https://docs.rms.legacy.com/db-integration'
  },
  {
    id: 'if-007',
    name: 'Mobile App Interface',
    direction: 'bidirectional',
    protocol: 'GraphQL',
    description: 'Field officer mobile application data sync',
    status: 'active',
    authentication: 'JWT',
    dataFormat: 'JSON',
    technicalDocumentationUrl: 'https://docs.mobile.msi.com/graphql-api'
  },
  {
    id: 'if-008',
    name: 'Third-Party Analytics Interface',
    direction: 'outbound',
    protocol: 'REST API',
    description: 'Data export for external analytics and reporting tools',
    status: 'planned',
    authentication: 'API Key',
    dataFormat: 'JSON',
    technicalDocumentationUrl: 'https://docs.analytics.partner.com/data-import'
  }
];

export const SAMPLE_INTEGRATION_MAP: IntegrationMap = {
  products: SAMPLE_PRODUCTS,
  connections: SAMPLE_CONNECTIONS
};

// Helper functions for the POC
export const getProductById = (id: string): Product | undefined => {
  return SAMPLE_PRODUCTS.find(product => product.id === id);
};

export const getConnectionsForProduct = (productId: string): Connection[] => {
  return SAMPLE_CONNECTIONS.filter(
    connection => connection.sourceProductId === productId || connection.targetProductId === productId
  );
};

export const getInternalProducts = (): Product[] => {
  return SAMPLE_PRODUCTS.filter(product => product.type === 'internal');
};

export const getExternalProducts = (): Product[] => {
  return SAMPLE_PRODUCTS.filter(product => product.type === 'external');
};

export const getProductsByCategory = (category: string): Product[] => {
  return SAMPLE_PRODUCTS.filter(product => product.category === category);
};

// Helper functions for interfaces
export const getAllInterfaces = (): Interface[] => {
  return SAMPLE_INTERFACES;
};

export const getInterfaceById = (id: string): Interface | undefined => {
  return SAMPLE_INTERFACES.find(iface => iface.id === id);
};

export const getActiveInterfaces = (): Interface[] => {
  return SAMPLE_INTERFACES.filter(iface => iface.status === 'active');
};

export const getInterfacesByProtocol = (protocol: string): Interface[] => {
  return SAMPLE_INTERFACES.filter(iface => iface.protocol === protocol);
};

export const getProductsConnectedByInterface = (interfaceObj: Interface): Product[] => {
  // Find connections that use this interface
  const connectionsWithInterface = SAMPLE_CONNECTIONS.filter(conn => conn.interface?.id === interfaceObj.id);
  
  // Extract unique product IDs from these connections
  const productIds = new Set<string>();
  connectionsWithInterface.forEach(conn => {
    productIds.add(conn.sourceProductId);
    productIds.add(conn.targetProductId);
  });
  
  // Return the actual product objects
  return Array.from(productIds).map(id => getProductById(id)).filter(Boolean) as Product[];
};

// CRUD Operations for Products
export const addProduct = (product: Omit<Product, 'id'>): Product => {
  // Generate a unique ID based on type and name
  const prefix = product.type === 'internal' ? 'msi' : 'ext';
  const timestamp = Date.now();
  const newProduct: Product = {
    ...product,
    id: `${prefix}-${timestamp}`
  };
  
  SAMPLE_PRODUCTS.push(newProduct);
  return newProduct;
};

export const updateProduct = (id: string, updates: Partial<Product>): Product | null => {
  const index = SAMPLE_PRODUCTS.findIndex(p => p.id === id);
  if (index !== -1) {
    SAMPLE_PRODUCTS[index] = { ...SAMPLE_PRODUCTS[index], ...updates, id }; // Preserve ID
    return SAMPLE_PRODUCTS[index];
  }
  return null;
};

export const deleteProduct = (id: string): boolean => {
  const index = SAMPLE_PRODUCTS.findIndex(p => p.id === id);
  if (index !== -1) {
    SAMPLE_PRODUCTS.splice(index, 1);
    
    // Also remove any connections involving this product
    for (let i = SAMPLE_CONNECTIONS.length - 1; i >= 0; i--) {
      if (SAMPLE_CONNECTIONS[i].sourceProductId === id || SAMPLE_CONNECTIONS[i].targetProductId === id) {
        SAMPLE_CONNECTIONS.splice(i, 1);
      }
    }
    
    return true;
  }
  return false;
};

// CRUD Operations for Interfaces
export const addInterface = (interfaceData: Omit<Interface, 'id'>): Interface => {
  // Generate a unique ID
  const timestamp = Date.now();
  const newInterface: Interface = {
    ...interfaceData,
    id: `iface-${timestamp}`
  };
  
  SAMPLE_INTERFACES.push(newInterface);
  return newInterface;
};

export const updateInterface = (id: string, updates: Partial<Interface>): Interface | null => {
  const index = SAMPLE_INTERFACES.findIndex(i => i.id === id);
  if (index !== -1) {
    SAMPLE_INTERFACES[index] = { ...SAMPLE_INTERFACES[index], ...updates, id }; // Preserve ID
    
    // Update any connections that use this interface
    SAMPLE_CONNECTIONS.forEach(conn => {
      if (conn.interface?.id === id) {
        conn.interface = SAMPLE_INTERFACES[index];
      }
    });
    
    return SAMPLE_INTERFACES[index];
  }
  return null;
};

export const deleteInterface = (id: string): boolean => {
  const index = SAMPLE_INTERFACES.findIndex(i => i.id === id);
  if (index !== -1) {
    SAMPLE_INTERFACES.splice(index, 1);
    
    // Remove interface reference from connections (but keep the connections)
    SAMPLE_CONNECTIONS.forEach(conn => {
      if (conn.interface?.id === id) {
        delete conn.interface;
      }
    });
    
    return true;
  }
  return false;
};

// CRUD Operations for Connections
export const addConnection = (connection: Omit<Connection, 'id'>): Connection => {
  const timestamp = Date.now();
  const newConnection: Connection = {
    ...connection,
    id: `conn-${timestamp}`
  };
  
  SAMPLE_CONNECTIONS.push(newConnection);
  return newConnection;
};

export const updateConnection = (id: string, updates: Partial<Connection>): Connection | null => {
  const index = SAMPLE_CONNECTIONS.findIndex(c => c.id === id);
  if (index !== -1) {
    SAMPLE_CONNECTIONS[index] = { ...SAMPLE_CONNECTIONS[index], ...updates, id }; // Preserve ID
    return SAMPLE_CONNECTIONS[index];
  }
  return null;
};

export const deleteConnection = (id: string): boolean => {
  const index = SAMPLE_CONNECTIONS.findIndex(c => c.id === id);
  if (index !== -1) {
    SAMPLE_CONNECTIONS.splice(index, 1);
    return true;
  }
  return false;
};
